"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import UserService, { User, UserStatusCode } from "../services/user-service";
import UserTable, { UserRow } from "../components/user-table";
import UserCreateModal, { UserFormValues } from "../components/user-create-modal";
import UserDetailModal from "../components/user-detail-modal";
import UserStatusConfirmModal from "../components/user-status-confirm-modal";
import UserResetPasswordModal from "../components/user-reset-password-modal";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";
import DepartmentService from "../../department/services/department-service";
import PrivilegeGroupService from "../services/privilege-group-service";
import BranchService from "../../branch/services/branch-service";
import UserInfoModal from "../components/user-creation-success-modal";
import UserImportModal from "../components/user-import-modal";
import UserAssignCustomerModal from "../components/user-assign-customer-modal";

function useDebounce<T>(value: T, delay = 500): T {
  const [val, setVal] = useState(value);
  useEffect(() => { const t = setTimeout(() => setVal(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return val;
}

const UserIndexPage: React.FC = () => {
  const [rows, setRows] = useState<UserRow[]>([]);
  const [tab, setTab] = useState<"active" | "inactive">("active");
  const [openCreate, setOpenCreate] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [editRow, setEditRow] = useState<UserRow | null>(null);
  const [editInitialValues, setEditInitialValues] = useState<UserFormValues | null>(null);
  const [detailRow, setDetailRow] = useState<UserRow | null>(null);
  const [detailData, setDetailData] = useState<User | undefined>();
  const [pendingStatus, setPendingStatus] = useState<{ row: UserRow; target: "active" | "inactive" } | null>(null);
  const [resetRow, setResetRow] = useState<UserRow | null>(null);
  const [assignCustomerRow, setAssignCustomerRow] = useState<UserRow | null>(null);
  const [departmentOptions, setDepartmentOptions] = useState<
    { label: string; value: number; status?: number }[]
  >([]);
  const [privilegeOptions, setPrivilegeOptions] = useState<{ label: string; value: number }[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);
  const [newUserInfo, setNewUserInfo] = useState<any>(null);
  const [keySearch, setKeySearch] = useState('');
  const [departmentId, setDepartmentId] = useState<number | undefined>();
  const [branchId, setBranchId] = useState<number | undefined>();
  const [branchOptions, setBranchOptions] = useState<{ label: string; value: number }[]>([]);
  const debouncedSearch = useDebounce(keySearch, 500);

  // services
  const { fetch: listUsers, loading: loadingList } = UserService.list();
  const { fetch: countTabs } = UserService.countTabs();
  const { fetch: listMyBranches } = BranchService.myBranches();
  const { fetch: createUser, loading: creating } = UserService.create();
  const { fetch: updateUser, loading: updating } = UserService.update();
  const { fetch: changeStatusUser, loading: changingStatus } = UserService.changeStatus();
  const { fetch: fetchDetail, loading: loadingDetail } = UserService.detail();
  const { fetch: resetPasswordRequest, loading: resettingPassword } = UserService.resetPassword();
  const { fetch: listDepartments } = DepartmentService.list();
  const { list: listPrivilegeGroups } = PrivilegeGroupService.list();

  const normalizeDateInput = (value?: string) => {
    if (!value) return "";
    if (value.includes("/")) {
      const [day, month, year] = value.split("/");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    return value.split("T")[0];
  };

  const listRef = useRef(listUsers);
  useEffect(() => {
    listRef.current = listUsers;
  }, [listUsers]);

  const departmentListRef = useRef(listDepartments);
  useEffect(() => {
    departmentListRef.current = listDepartments;
  }, [listDepartments]);

  const privilegeListRef = useRef(listPrivilegeGroups);
  useEffect(() => {
    privilegeListRef.current = listPrivilegeGroups;
  }, [listPrivilegeGroups]);

  const listMyBranchesRef = useRef(listMyBranches);
  useEffect(() => {
    listMyBranchesRef.current = listMyBranches;
  }, [listMyBranches]);

  const load = useCallback(async () => {
    // Filter status theo tab để backend phân trang đúng (không fetch -1 rồi filter client-side)
    const statusFilter = tab === "active" ? UserStatusCode.ACTIVE : UserStatusCode.INACTIVE;
    const res = await listRef.current({ status: statusFilter, page, limit, key_search: debouncedSearch, department_id: departmentId, branch_id: branchId });
    if (res && isSuccess(res)) {
      const list = res.data?.list ?? [];
      const d: any = res.data as any;
      const total = Number(d?.total_record ?? d?.totalRecord ?? d?.total ?? d?.totalRecords ?? list.length ?? 0);
      setTotalRecords(total);
      setRows(
        list.map((u: any) => ({
          id: u.id,
          username: u.username ?? u.username ?? "",
          name: u.name,
          email: u.email,
          phone: u.phone ?? "",
          department_id:
            u.department_id && Number(u.department_id) !== 0
              ? u.department_id
              : null,
          department_name: u.department_name,
          privilege_group_id:
            u.privilege_group_id && Number(u.privilege_group_id) !== 0
              ? u.privilege_group_id
              : null,
          privilege_group_name: u.privilege_group_name,
          gender: u.gender ?? 0,
          address: u.address ?? "",
          avatar: u.avatar ?? "",
          id_card_number: u.id_card_number ?? u.identity_card ?? "",
          customer_ids: u.customer_ids ?? "",
          is_driver: Number(u.is_driver) === 1 ? 1 : 0,
          is_counter: Number(u.is_counter) === 1 ? 1 : 0,
          is_all_branch: Number(u.is_all_branch) === 1 ? 1 : 0,
          branches: Array.isArray(u.branches)
            ? u.branches
                .filter(Boolean)
                .map((b: any) => ({ id: Number(b.id), name: b.name ?? `Chi nhánh #${b.id}` }))
            : [],
          birthday: u.birthday,
          joining_date: u.joining_date,
          created_at: u.created_at,
          statusCode: Number(u.status) as UserStatusCode,
        }))
      );
    }
  }, [page, limit, debouncedSearch, departmentId, branchId, tab]);

  useEffect(() => { void load(); }, [load]);

  // Reset page về 1 khi đổi tab/search/department/chi nhánh để tránh show trang trống
  useEffect(() => {
    setPage(1);
  }, [tab, debouncedSearch, departmentId, branchId]);

  // Load số lượng user cho 2 tab (active/inactive) để hiển thị badge
  const countTabsRef = useRef(countTabs);
  useEffect(() => { countTabsRef.current = countTabs; }, [countTabs]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await countTabsRef.current({
        key_search: debouncedSearch,
        department_id: departmentId,
        branch_id: branchId,
      });
      if (!cancelled && res && isSuccess(res)) {
        const d: any = res.data || {};
        setActiveCount(Number(d.total_active ?? 0));
        setInactiveCount(Number(d.total_locked ?? 0));
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedSearch, departmentId, branchId, rows]);

  useEffect(() => {
    let cancelled = false;
    const fetchMeta = async () => {
      try {
        const [deptRes, privRes, branchRes] = await Promise.all([
          departmentListRef.current({ status: 1 }),
          privilegeListRef.current({ status: 1 }),
          listMyBranchesRef.current(),
        ]);

        if (!cancelled && branchRes && isSuccess(branchRes)) {
          const raw: any = branchRes.data;
          const arr: any[] = Array.isArray(raw) ? raw : (raw?.list ?? raw?.data ?? []);
          setBranchOptions(
            arr.filter(Boolean).map((b: any) => ({ label: b.name ?? `Chi nhánh #${b.id}`, value: Number(b.id) })),
          );
        }

        if (!cancelled && deptRes && isSuccess(deptRes)) {
          setDepartmentOptions(
            Array.isArray(deptRes.data)
              ? deptRes.data.map((d: any) => ({
                  label: d.name ?? `Phòng ban #${d.id}`,
                  value: d.id,
                  status: Number(d.status ?? 1),
                  is_driver: Number(d.is_driver ?? 0),
                }))
              : []
          );
        }

        if (!cancelled && privRes && isSuccess(privRes)) {
          setPrivilegeOptions(
            (privRes.data?.list ?? []).map((p: any) => ({
              label: p.name,
              value: p.id,
            }))
          );
        }
      } catch (error) {
        if (!cancelled) console.error(error);
      }
    };

    void fetchMeta();

    return () => {
      cancelled = true;
    };
  }, []);

  // Backend đã filter theo status → rows chính là data của tab hiện tại, không cần filter client-side
  const visibleRows = rows;
  
  const formatDateToDDMMYYYY = (dateStr?: string | null): string | undefined => {
    if (!dateStr) return undefined;
    const [year, month, day] = dateStr.split("-");
    if (!year || !month || !day) return undefined;
    return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
  };
  
  const onCreate = async (v: UserFormValues): Promise<boolean> => {
    const payload = {
      username: v.username.trim(),
      department_id: v.department_id ?? undefined,
      privilege_group_id: v.privilege_group_id ?? undefined,
      name: v.name.trim(),
      email: v.email.trim(),
      phone: v.phone.trim(),
      password: v.password.trim(),
      address: v.address.trim(),
      avatar: v.avatar ?? "",
      gender: v.gender,
      joining_date: (formatDateToDDMMYYYY(v.joining_date)) || undefined,
      birthday: (formatDateToDDMMYYYY(v.birthday)) || undefined,
      id_card_number: v.id_card_number?.trim() || undefined,
      status: v.isActive ? UserStatusCode.ACTIVE : UserStatusCode.INACTIVE,
      branch_ids: v.branch_ids ?? [],
      attendance_mode: v.attendance_mode,
    };

    const res = await createUser(payload);
    if (res && isSuccess(res)) {
      showToast({ message: "Tạo người dùng thành công", type: "success" });
      setNewUserInfo(res.data);
      setShowUserInfoModal(true);
      void load();
      return true;
    }
    showToast({ message: res?.message ?? "Không thể tạo người dùng", type: "error" });
    return false;
  };

  const onEditSubmit = async (v: UserFormValues): Promise<boolean> => {
    if (!editRow) return false;
    const payload = {
      username: v.username.trim(),
      department_id: v.department_id ?? undefined,
      privilege_group_id: v.privilege_group_id ?? undefined,
      name: v.name.trim(),
      email: v.email.trim(),
      phone: v.phone.trim(),
      password: v.password?.trim() ? v.password.trim() : undefined,
      address: v.address.trim(),
      avatar: v.avatar ?? undefined,
      gender: v.gender,
      joining_date: formatDateToDDMMYYYY(v.joining_date) || undefined,
      birthday: formatDateToDDMMYYYY(v.birthday) || undefined,
      id_card_number: v.id_card_number?.trim() || undefined,
      status: v.isActive ? UserStatusCode.ACTIVE : UserStatusCode.INACTIVE,
      branch_ids: v.branch_ids ?? [],
      attendance_mode: v.attendance_mode,
    };
    const res = await updateUser(editRow.id, payload);
    if (res && isSuccess(res)) {
      showToast({ message: "Cập nhật người dùng thành công", type: "success" });
      setEditRow(null);
      setEditInitialValues(null);
      void load();
      return true;
    }
    showToast({ message: res?.message ?? "Không thể cập nhật", type: "error" });
    return false;
  };

  const handleEdit = async (row: UserRow) => {
    try {
      const res = await fetchDetail(row.id);
      if (res && isSuccess(res) && res.data) {
        const data = res.data;
        setEditInitialValues({
          username: data.username ?? (data as any)?.username ?? "",
          name: data.name ?? "",
          email: data.email ?? "",
          phone: data.phone ?? "",
          department_id:
            data.department_id && Number(data.department_id) !== 0
              ? data.department_id
              : null,
          privilege_group_id:
            data.privilege_group_id && Number(data.privilege_group_id) !== 0
              ? data.privilege_group_id
              : null,
          address: data.address ?? "",
          gender: data.gender ?? 0,
          joining_date: normalizeDateInput(
            data.joining_date ?? (data as any)?.joining_date ?? (data as any)?.joiningDate
          ),
          birthday: normalizeDateInput(data.birthday ?? (data as any)?.birth_date),
          password: "",
          isActive: Number(data.status) === UserStatusCode.ACTIVE,
          avatar: data.avatar ?? "",
          id_card_number: (data as any)?.id_card_number ?? (data as any)?.identity_card ?? "",
          branch_ids:
          (data as any).is_all_branch === 1
            ? [-1]
            : Array.isArray((data as any).branches)
              ? (data as any).branches.map((b: any) => b.id)
              : [],
          attendance_mode: (data as any)?.attendance_mode ?? null,
        });
        setEditRow(row);
      } else {
        showToast({
          message: res?.message ?? "Không thể tải thông tin nhân viên",
          type: "error",
        });
      }
    } catch (error) {
      showToast({
        message:
          error instanceof Error
            ? error.message
            : "Không thể tải thông tin nhân viên",
        type: "error",
      });
    }
  };

  const handleViewDetail = async (row: UserRow) => {
    setDetailRow(row);
    setDetailData(undefined);
    const res = await fetchDetail(row.id);
    if (res && isSuccess(res)) setDetailData(res.data);
    else setDetailRow(null);
  };

  const handleCloseDetailModal = () => {
    setDetailRow(null);
    setDetailData(undefined);
  };

  const onRequestDeactivate = (row: UserRow) => setPendingStatus({ row, target: "inactive" });
  const onRequestActivate = (row: UserRow) => setPendingStatus({ row, target: "active" });

  const onConfirmChangeStatus = async () => {
    if (!pendingStatus) return;
    const next = pendingStatus.target === "active" ? UserStatusCode.ACTIVE : UserStatusCode.INACTIVE;
    const res = await changeStatusUser(pendingStatus.row.id, { status: next });
    if (res && isSuccess(res)) {
      showToast({
        message: pendingStatus.target === "active" ? "Đã kích hoạt người dùng" : "Đã ngưng người dùng",
        type: "success",
      });
      setPendingStatus(null);
      void load();
      return;
    }
    showToast({ message: res?.message ?? "Không thể cập nhật trạng thái", type: "error" });
    setPendingStatus(null);
  };

  const onSubmitResetPassword = async (): Promise<boolean> => {
    if (!resetRow) return false;
    const res = await resetPasswordRequest(resetRow.id);
    if (res && isSuccess(res)) {
      showToast({ message: "Đặt lại mật khẩu thành công", type: "success" });
      setNewUserInfo({
        username: (res.data as any)?.username ?? "",
        password: (res.data as any)?.password ?? "",
        totp_qr_code: (res.data as any)?.totp_qr_code ?? "",
      });
      setShowUserInfoModal(true);
      setResetRow(null);
      return true;
    }
    showToast({ message: res?.message ?? "Không thể đặt lại mật khẩu", type: "error" });
    return false;
  };

  return (
    <div className="min-h-screen bg-gray-50/50 p-6 font-sans space-y-6">
      
      {/* --- HEADER --- */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
           <h1 className="text-2xl font-extrabold text-green-600 tracking-tight">
             Danh sách nhân viên
           </h1>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={() => setOpenImport(true)}
            className="group inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-blue-200 transition-all hover:bg-blue-700 hover:-translate-y-0.5"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            <span>NHẬP EXCEL</span>
          </button>
          <button
            onClick={() => {
              setEditInitialValues(null);
              setOpenCreate(true);
            }}
            className="group inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-green-200 transition-all hover:bg-green-700 hover:-translate-y-0.5"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 transition-transform group-hover:rotate-90">
              <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
            </svg>
            <span>THÊM NHÂN VIÊN</span>
          </button>
        </div>
      </div>

      {/* --- MAIN CARD --- */}
      <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
        
        {/* --- TOOLBAR --- */}
        <div className="px-6 py-5 border-b border-gray-100 flex flex-col xl:flex-row xl:items-center justify-between gap-5">
          
          {/* Tabs */}
          <div className="flex items-center gap-6">
            <button 
              onClick={() => setTab("active")} 
              className={`group relative flex items-center gap-2.5 pb-3 text-sm font-bold uppercase tracking-wide transition-all ${
                tab === "active" ? "text-green-700" : "text-gray-400 hover:text-gray-600"
              }`}
            >
              <span>Đang hoạt động</span>
              <span className={`rounded-md px-2 py-0.5 text-xs font-bold transition-colors ${
                 tab === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
              }`}>
                {activeCount}
              </span>
              <span className={`absolute bottom-0 left-0 h-[3px] w-full rounded-t-full bg-green-600 transition-transform duration-300 ${
                 tab === "active" ? "scale-x-100" : "scale-x-0"
              }`} />
            </button>

            <button 
              onClick={() => setTab("inactive")} 
              className={`group relative flex items-center gap-2.5 pb-3 text-sm font-bold uppercase tracking-wide transition-all ${
                tab === "inactive" ? "text-green-700" : "text-gray-400 hover:text-gray-600"
              }`}
            >
              <span>Tạm ngưng</span>
              <span className={`rounded-md px-2 py-0.5 text-xs font-bold transition-colors ${
                 tab === "inactive" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
              }`}>
                {inactiveCount}
              </span>
              <span className={`absolute bottom-0 left-0 h-[3px] w-full rounded-t-full bg-green-600 transition-transform duration-300 ${
                 tab === "inactive" ? "scale-x-100" : "scale-x-0"
              }`} />
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">

            {/* Branch Select — chỉ chi nhánh user trực thuộc */}
            <div className="relative w-full sm:w-[220px]">
               <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path fillRule="evenodd" d="M4 16.5v-13h-.25a.75.75 0 010-1.5h12.5a.75.75 0 010 1.5H16v13h.25a.75.75 0 010 1.5h-3.5a.75.75 0 01-.75-.75v-2.5a.75.75 0 00-.75-.75h-2a.75.75 0 00-.75.75v2.5a.75.75 0 01-.75.75h-3.5a.75.75 0 010-1.5H4zm3-11a.75.75 0 01.75-.75h.5a.75.75 0 010 1.5h-.5A.75.75 0 017 5.5zm2.75-.75a.75.75 0 000 1.5h.5a.75.75 0 000-1.5h-.5zM7 8.25a.75.75 0 01.75-.75h.5a.75.75 0 010 1.5h-.5A.75.75 0 017 8.25zm2.75-.75a.75.75 0 000 1.5h.5a.75.75 0 000-1.5h-.5zM7 11a.75.75 0 01.75-.75h.5a.75.75 0 010 1.5h-.5A.75.75 0 017 11zm2.75-.75a.75.75 0 000 1.5h.5a.75.75 0 000-1.5h-.5z" clipRule="evenodd" />
                  </svg>
               </div>
               <select
                  value={branchId ?? ""}
                  onChange={(e) => setBranchId(Number(e.target.value) || undefined)}
                  className="w-full appearance-none rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-10 py-2.5 text-sm font-medium text-gray-700 shadow-sm focus:border-green-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all cursor-pointer hover:bg-gray-100"
               >
                  <option value="">Tất cả chi nhánh</option>
                  {branchOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
               </select>
               <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" /></svg>
               </div>
            </div>

            {/* Department Select */}
            <div className="relative w-full sm:w-[220px]">
               <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path fillRule="evenodd" d="M2.628 1.601C5.028 1.206 7.49 1 10 1s4.973.206 7.372.601a.75.75 0 01.628.74v2.288a2.25 2.25 0 01-.659 1.59l-4.682 4.683a2.25 2.25 0 00-.659 1.59v3.037c0 .684-.31 1.33-.844 1.757l-1.937 1.55A.75.75 0 018 18.25v-5.757a2.25 2.25 0 00-.659-1.591L2.659 6.22A2.25 2.25 0 012 4.629V2.34a.75.75 0 01.628-.74z" clipRule="evenodd" />
                  </svg>
               </div>
               <select
                  value={departmentId}
                  onChange={(e) => setDepartmentId(Number(e.target.value) || undefined)}
                  className="w-full appearance-none rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-10 py-2.5 text-sm font-medium text-gray-700 shadow-sm focus:border-green-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all cursor-pointer hover:bg-gray-100"
               >
                  <option value="">Tất cả bộ phận</option>
                  {departmentOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
               </select>
               <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" /></svg>
               </div>
            </div>

            {/* Search Input */}
            <div className="relative w-full sm:w-[320px]">
               <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                  </svg>
               </div>
               <input 
                  type="text"
                  value={keySearch} 
                  onChange={(e) => setKeySearch(e.target.value)} 
                  placeholder="Tìm kiếm tên, SĐT nhân viên..." 
                  className="w-full rounded-xl border border-gray-200 bg-white pl-9 pr-20 py-2.5 text-sm shadow-sm placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all" 
               />
               <button
                  onClick={() => load()}
                  className="absolute right-1.5 top-1.5 bottom-1.5 px-3 bg-green-50 text-green-600 rounded-lg text-xs font-bold hover:bg-green-100 transition-colors"
               >
                  TÌM
               </button>
            </div>
          </div>
        </div>

        {/* --- TABLE AREA --- */}
        <div className="p-0">
          {tab === "active" ? (
            <UserTable
              rows={visibleRows}
              isLoading={loadingList}
              startIndex={(page - 1) * limit + 1}
              onEdit={handleEdit}
              onDeactivate={onRequestDeactivate}
              onViewDetail={handleViewDetail}
              onResetPassword={setResetRow}
              onAssignCustomer={setAssignCustomerRow}
            />
          ) : (
            <UserTable
              rows={visibleRows}
              isLoading={loadingList}
              startIndex={(page - 1) * limit + 1}
              onEdit={handleEdit}
              onActivate={onRequestActivate}
              onViewDetail={handleViewDetail}
              onResetPassword={setResetRow}
              onAssignCustomer={setAssignCustomerRow}
            />
          )}
        </div>

        {/* --- PAGINATION (FOOTER) --- */}
        <div className="border-t border-gray-100 bg-gray-50/50 px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-500 font-medium">
              {totalRecords > 0
                ? `Hiển thị ${(page - 1) * limit + 1} - ${(page - 1) * limit + visibleRows.length} trong tổng số ${totalRecords} nhân viên`
                : "Chưa có dữ liệu"}
            </div>
            
            <div className="flex items-center gap-3">
              {/* Limit Select */}
              <select
                className="rounded-lg border-gray-200 bg-white py-1.5 pl-3 pr-8 text-sm font-medium text-gray-700 shadow-sm focus:border-green-500 focus:ring-green-500 cursor-pointer"
                value={limit}
                onChange={(e) => { setPage(1); setLimit(Number(e.target.value)); }}
              >
                <option value={20}>20 hàng</option>
                <option value={50}>50 hàng</option>
                <option value={100}>100 hàng</option>
              </select>

              {/* Page Buttons */}
              <div className="flex items-center gap-1">
                <button
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" /></svg>
                </button>
                
                {(() => {
                  const totalPages = Math.max(1, Math.ceil(totalRecords / limit));
                  const pages: (number | string)[] = [];
                  const add = (p: number | string) => pages.push(p);
                  const start = Math.max(1, page - 1);
                  const end = Math.min(totalPages, page + 1);
                  
                  if (start > 1) add(1);
                  if (start > 2) add("...");
                  for (let p = start; p <= end; p++) add(p);
                  if (end < totalPages - 1) add("...");
                  if (end < totalPages) add(totalPages);

                  return pages.map((p, idx) => (
                    typeof p === "number" ? (
                      <button
                        key={idx}
                        onClick={() => setPage(p)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-bold transition-all ${
                          p === page 
                          ? "bg-green-600 text-white shadow-md shadow-green-200" 
                          : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {p}
                      </button>
                    ) : (
                      <span key={idx} className="px-1 text-gray-400 text-xs">...</span>
                    )
                  ));
                })()}

                <button
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  disabled={page >= Math.max(1, Math.ceil(totalRecords / limit))}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" /></svg>
                </button>
              </div>
            </div>
        </div>
      </div>

      {/* --- MODALS --- */}
      <UserImportModal
        isOpen={openImport}
        onClose={() => setOpenImport(false)}
        onSuccess={() => void load()}
      />

      {openCreate && (
        <UserCreateModal
          onClose={() => setOpenCreate(false)}
          onSubmit={onCreate}
          isSubmitting={creating}
          departmentOptions={departmentOptions}
          privilegeOptions={privilegeOptions}
          mode="create"
        />
      )}

      {editRow && editInitialValues && (
        <UserCreateModal
          onClose={() => {
            setEditRow(null);
            setEditInitialValues(null);
          }}
          onSubmit={onEditSubmit}
          isSubmitting={updating}
          titleText="Cập nhật nhân viên"
          submitText="Cập nhật"
          departmentOptions={departmentOptions}
          privilegeOptions={privilegeOptions}
          initialValues={editInitialValues}
          mode="edit"
        />
      )}

      {pendingStatus && (
        <UserStatusConfirmModal
          mode={pendingStatus.target === "inactive" ? "deactivate" : "activate"}
          userName={pendingStatus.row.name}
          onCancel={() => setPendingStatus(null)}
          onConfirm={onConfirmChangeStatus}
          isProcessing={changingStatus}
        />
      )}

      {detailRow && detailData && (
        <UserDetailModal
          userName={detailRow.name}
          data={detailData}
          loading={loadingDetail}
          onClose={handleCloseDetailModal}
        />
      )}

      {resetRow && (
        <UserResetPasswordModal
          userName={resetRow.name}
          onClose={() => setResetRow(null)}
          onSubmit={onSubmitResetPassword}
          isSubmitting={resettingPassword}
        />
      )}

      {showUserInfoModal && newUserInfo && (
        <UserInfoModal
          username={newUserInfo.username}
          password={newUserInfo.password}
          totp_qr_code={newUserInfo.totp_qr_code}
          onClose={() => {
            setShowUserInfoModal(false);
            setNewUserInfo(null);
          }}
        />
      )}

      {assignCustomerRow && (
        <UserAssignCustomerModal
          user={assignCustomerRow}
          isOpen={!!assignCustomerRow}
          onClose={() => setAssignCustomerRow(null)}
          onSuccess={() => void load()}
        />
      )}
    </div>
  );
};
export default UserIndexPage;